import './KnownIssues.scss';
import { Box, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useTheme } from '@mui/system';
import AppAccordion from '../../../components/AppAccordion/AppAccordion';
import Issue from './Issue/Issue';
import knownIssuesImage from '../../../assets/images/knownIssuesImg.jpeg';
import useHandleAnchorClick from '../../../hooks/useHandleAnchorClick';
import { useTranslation } from 'react-i18next';
import KnownIssuesSearch from './knownIssueSearch';
import KnownIssuesFilterDropdown from './knownIssueFilter';
import KnownIssuesSortDropdown from './knownIssueSort';

interface KnownIssueEntry {
  affected_api: string | { value: string }[];
  status?: string;
  environments?: string | string[];
  ticket_id?: string;
  last_updated?: string;
  targeted_fix_date?: string;
  [key: string]: any;
}

interface KnownIssue {
  accordion: { details: string; summary: string }[];
  entries: KnownIssueEntry[];
  header: string;
}

interface StaticContent {
  title: string;
  operatingCar: string;
  link: {
    title: string;
  };
  affectedApisDropdown: {
    label: string;
    items: string[];
  };
  statuses: { [key: string]: string };
  environments: { [key: string]: string };
  filterLabel: string;
  filterDRop: string[];
  showAll: string;
  sort: string;
  issueTitle: string[];
}

const KnownIssues = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { t } = useTranslation();

  const knownIssue = t('known_issues_new_content', {
    returnObjects: true
  })[0] as KnownIssue;
  const accordion = knownIssue?.accordion
    ? knownIssue.accordion[0]
    : { details: '', summary: '' };
  const staticContent = t('knownIssues', {
    returnObjects: true
  }) as StaticContent;

  // const [selectedApis, setSelectedApis] = useState<string[]>([showAllText]);
  const [accordionExpanded, setAccordionExpanded] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    affectedApis: [staticContent.showAll],
    status: [staticContent.showAll],
    environments: [staticContent.showAll]
  });
  const [selectedSort, setSelectedSort] = useState('');

  const parser = new DOMParser();
  const linkContent: Document = parser.parseFromString(
    staticContent.link.title,
    'text/html'
  );
  const linkText = linkContent.body.innerHTML.replaceAll(
    'href="http',
    "target='_blank' href=\"http"
  );

  const sortIssues = (issues: KnownIssueEntry[]) => {
    if (!Array.isArray(issues)) {
      return [];
    }
    const sorted = [...issues];

    const extraFirstTicketNumber = (ticketString) => {
      const match = (ticketString || '').match(/\d+/);
      return match ? parseInt(match[0],10) : 0;
    };

    switch (selectedSort) {
      case 'ticket_number_asc':
        return sorted.sort((a, b) => {
          const numA = extraFirstTicketNumber(a.ticket_id);
          const numB = extraFirstTicketNumber(b.ticket_id);
          return numA -numB;
        });
      case 'ticket_number_desc':
        return sorted.sort((a, b) => {
          const numA = extraFirstTicketNumber(a.ticket_id);
          const numB = extraFirstTicketNumber(b.ticket_id);
          return numB -numA;
        });
      case 'last_updated_asc':
        return sorted.sort((a, b) => {
          const dateA = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          const dateB = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return dateA - dateB;
        });
      case 'last_updated_desc':
        return sorted.sort((a, b) => {
          const dateA = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          const dateB = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return dateB - dateA;
        });
      case 'targeted_fix_date_asc':
        return sorted.sort((a, b) => {
          // Check if dates exist and are not empty
          const hasDateA =
            a.targeted_fix_date && a.targeted_fix_date.trim() !== '';
          const hasDateB =
            b.targeted_fix_date && b.targeted_fix_date.trim() !== '';

          // If both don't have dates (will show TBC), maintain original order
          if (!hasDateA && !hasDateB) return 0;

          // TBC values (missing dates) go to the end in ascending order
          if (!hasDateA) return 1;
          if (!hasDateB) return -1;

          // Both have dates, compare them
          const dateA = a.targeted_fix_date
            ? new Date(a.targeted_fix_date).getTime()
            : 0;
          const dateB = b.targeted_fix_date
            ? new Date(b.targeted_fix_date).getTime()
            : 0;
          return dateA - dateB;
        });
      case 'targeted_fix_date_desc':
        return sorted.sort((a, b) => {
          // Check if dates exist and are not empty
          const hasDateA =
            a.targeted_fix_date && a.targeted_fix_date.trim() !== '';
          const hasDateB =
            b.targeted_fix_date && b.targeted_fix_date.trim() !== '';

          // If both don't have dates (will show TBC), maintain original order
          if (!hasDateA && !hasDateB) return 0;

          // TBC values (missing dates) go to the end in descending order
          if (!hasDateA) return 1;
          if (!hasDateB) return -1;

          // Both have dates, compare them
          const dateA = new Date(a.targeted_fix_date ?? '').getTime();
          const dateB = new Date(b.targeted_fix_date ?? '').getTime();
          return dateB - dateA;
        });
      default:
        return sorted;
    }
  };

  // Helper function to get the display status (same logic as in Issue component)
  const getDisplayedStatus = (issue: KnownIssueEntry): string | undefined => {
    if (
      issue.status === staticContent.statuses.resolved &&
      issue.last_updated
    ) {
      const [year, month, day] = issue.last_updated.split('-').map(Number);
      const resolvedDate = new Date(Date.UTC(year, month - 1, day));
      const now = new Date();
      const diffDays = Math.floor(
        (now.getTime() - resolvedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays > 90) {
        return staticContent.statuses.archived;
      }
    }
    return issue.status;
  };

  const ticketIdToTitle: Record<string, string> = {};
    (knownIssue.entries || []).forEach((entry, idx) => {
      if (entry.ticket_id && staticContent.issueTitle[idx]) {
        ticketIdToTitle[entry.ticket_id] = staticContent.issueTitle[idx];
      }
  });

  const getFilteredIssues = () => {
    const entries = knownIssue.entries || [];

    // Filter by search text
    let filtered = entries;
    if (searchText.trim()) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter((entry) => {
        if (
          getDisplayedStatus(entry) === staticContent.statuses.archived &&
          lowerSearch.includes(staticContent.statuses.resolved.toLowerCase())
        ) {
          return false;
        }
 
        // Helper function to search in a value recursively
        const searchInValue = (val: any): boolean => {
          if (typeof val === 'string') {
            return val.toLowerCase().includes(lowerSearch);
          } else if (Array.isArray(val)) {
            return val.some((item) => searchInValue(item));
          } else if (val && typeof val === 'object' && val !== null) {
            // Handle objects with value property
            if ('value' in val && typeof val.value === 'string') {
              return val.value.toLowerCase().includes(lowerSearch);
            }
            // Search in all object properties
            return Object.values(val).some((nestedVal) =>
              searchInValue(nestedVal)
            );
          }
          return false;
        };

        // Search in all entry values (general search)
        const generalSearch = [
          ...Object.entries(entry)
            .filter(([key]) => !['workaround', 'use_case', 'error_message', 'status', 'statuses'].includes(key))
            .map(([, val]) => val),
          getDisplayedStatus(entry)
        ].some((val) => searchInValue(val));
 
 

        // Explicitly search in fields that are actually used in the Issue component
        const specificFieldSearch = [
          // Ticket fields
          entry.ticket_id,
          entry.ticket,
          entry.ticketId,

          // Date fields (as used in Issue component)
          entry.last_updated,
          entry.targeted_fix_date,
          entry.date,
          entry.created_date,
          entry.updated_date,

          // Content fields (as destructured in Issue component)
          entry.affected_api,
          entry.details,
          entry.environment,
          entry.error_message,
          entry.summary,
          entry.use_case,
          entry.workaround,
          getDisplayedStatus(entry),
          entry.ticket_id ? ticketIdToTitle[entry.ticket_id] : undefined
 
        ].some((field) => {
          if (!field) return false;
          return searchInValue(field);
        });

        // Also search in formatted dates (since dates are formatted for display)
        const dateFieldSearch = [
          entry.last_updated,
          entry.targeted_fix_date
        ].some((dateField) => {
          if (!dateField || typeof dateField !== 'string') {
            // Only match if searching for TBC
            return lowerSearch === 'tbc';
          }
         
          // Match TBC (case-insensitive, allow spaces)
          if (dateField.trim().toLowerCase().replace(/\s+/g, '') === 'tbc' && lowerSearch.replace(/\s+/g, '') === 'tbc') return true;
          if (dateField.toLowerCase().includes('tbc') && lowerSearch.includes('tbc')) return true;

          // Search in raw date format (YYYY-MM-DD)
          if (dateField.toLowerCase().includes(lowerSearch)) return true;

          // Search in formatted date (simulate the formatDate function)
          try {
            const [year, month, day] = dateField.split('-').map(Number);
            if (year && month && day) {
              const date = new Date(Date.UTC(year, month - 1, day));

              // English month
              const enMonth = date.toLocaleString('en-US', { month: 'long' });
              // French month
              const frMonth = date.toLocaleString('fr-FR', { month: 'long' });

              // US and non-US formats, both English and French
              const formats = [
                `${enMonth} ${day}, ${year}`,
                `${day} ${enMonth} ${year}`,
                `${frMonth} ${day}, ${year}`,
                `${day} ${frMonth} ${year}`
              ];

              return formats.some(f =>
                f.toLowerCase().includes(lowerSearch)
              );
            }
          } catch (error) {
            // If date parsing fails, just continue
          }

          return false;
        });

        return generalSearch || specificFieldSearch || dateFieldSearch;
      });
    }

    // Filter by affectedApis
    if (
      selectedFilters.affectedApis.length &&
      !selectedFilters.affectedApis.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const affectedApi = issue.affected_api;

        return selectedFilters.affectedApis.some((api) => {
          if (typeof affectedApi === 'string') {
            return affectedApi.includes(api);
          } else if (Array.isArray(affectedApi)) {
            return affectedApi.some((item) => {
              if (typeof item === 'string') {
                return (item as string).includes(api);
              } else if (item && typeof item === 'object' && 'value' in item) {
                return (item as { value: string }).value.includes(api);
              }
              return false;
            });
          }
          return false;
        });
      });
    }

    // Filter by status
    if (
      selectedFilters.status.length &&
      !selectedFilters.status.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const displayedStatus = getDisplayedStatus(issue);
        return (
          displayedStatus && selectedFilters.status.includes(displayedStatus)
        );
      });
    }

    // Filter by environment
    if (
      selectedFilters.environments.length &&
      !selectedFilters.environments.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const environment = issue.environment; // Changed from environments to environment

        const isMatch = selectedFilters.environments.some((selectedEnv) => {
          if (typeof environment === 'string') {
            // Handle partial matching for environments like "Prod" matching "Production"
            const match =
              environment.toLowerCase().includes(selectedEnv.toLowerCase()) ||
              selectedEnv.toLowerCase().includes(environment.toLowerCase());
            return match;
          } else if (Array.isArray(environment)) {
            const match = environment.some((env) => {
              const envMatch =
                env.toLowerCase().includes(selectedEnv.toLowerCase()) ||
                selectedEnv.toLowerCase().includes(env.toLowerCase());
              return envMatch;
            });
            return match;
          }
          return false;
        });
        return isMatch;
      });
    }

    return sortIssues(filtered);
  };

  const handleAnchorClick = useHandleAnchorClick();

  const handleAccordionChange = (isExpanded: boolean) => {
    setAccordionExpanded(isExpanded);
  };

  const handleSortChange = (sortValue: string) => {
    setSelectedSort(sortValue);
  };

  return (
    <Box className="known-issues">
      {isDesktop ? (
        <div className="container-header">
          <div dangerouslySetInnerHTML={{ __html: knownIssue?.header || '' }} />
        </div>
      ) : (
        <>
          <img alt="Known Issues" src={knownIssuesImage} width="100%" />
          <h1 className="title-mobile">{staticContent.title}</h1>
        </>
      )}

      <Box className={isDesktop ? 'container' : 'container container--mobile'}>
        <AppAccordion
          details={accordion.details}
          summary={accordion.summary}
          expanded={accordionExpanded}
          onChange={handleAccordionChange}
        />

        {/* <AppMultiSelect
          label={staticContent.affectedApisDropdown.label}
          itemList={staticContent.affectedApisDropdown.items}
          values={selectedApis}
          width={250}
          handleOnChange={handleOnChangeSelect}
        /> */}

        <Box
           sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1
            }
          }}
        >
          {/* Search on the left */}
          <KnownIssuesSearch
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            staticContent={staticContent}
          />

          {/* Filter and Sort together on the right */}
           <section aria-label="Sort and filter">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              flexShrink: 0,
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                gap: 2,
                width: '100%'
              }
            }}
          >
            <KnownIssuesFilterDropdown
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              affectedApis={staticContent.affectedApisDropdown?.items || []}
              status={Object.values(staticContent.statuses)}
              environments={Object.values(staticContent.environments || {})}
              staticContent={staticContent}
            />
            <KnownIssuesSortDropdown
              selectedSort={selectedSort}
              onSortChange={handleSortChange}
              staticContent={staticContent}
            />
          </Box>
          </section>
        </Box>

        {getFilteredIssues().map((issue, index) => {
           const issueTitl =
           issue.ticket_id && ticketIdToTitle[issue.ticket_id]
             ? ticketIdToTitle[issue.ticket_id]
             : '';
          return (
            <Issue
              key={index}
              issue={issue}
              issuesIndex={index}
              staticContent={staticContent}
              issueTitl={issueTitl}
            />
          );
        })}
      </Box>
      <Box className="banner">
        <div
          dangerouslySetInnerHTML={{
            __html: linkText
          }}
          onClick={handleAnchorClick}
        />
      </Box>
    </Box>
  );
};

export default KnownIssues;
